'use client';
import React from 'react';
import { SlidersHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
import type { FilterState } from './BoardScreen';
import { PROBLEM_CATEGORIES } from './ProblemFeed';

const CATEGORIES = ['All', ...PROBLEM_CATEGORIES];
const STATUSES = ['All', 'open', 'under-review', 'funded', 'closed'];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'most-proposals', label: 'Most Proposals' },
  { value: 'most-funded', label: 'Most Funded' },
  { value: 'top-rated', label: 'Top Rated Posters' },
];

const STATUS_LABELS: Record<string, string> = {
  All: 'All Statuses',
  open: 'Open',
  'under-review': 'Under Review',
  funded: 'Funded',
  closed: 'Closed',
};

interface FilterPanelProps {
  filters: FilterState;
  updateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
  isOpen: boolean;
  onToggle: () => void;
  totalCount: number;
}

export default function FilterPanel({ filters, updateFilter, isOpen, onToggle, totalCount }: FilterPanelProps) {
  return (
    <aside
      className={`shrink-0 border-r border-border bg-card transition-all duration-300 ease-in-out overflow-hidden flex flex-col ${
        isOpen ? 'w-56 xl:w-64' : 'w-12'
      } hidden lg:flex`}
      style={{ minHeight: 'calc(100vh - 64px)' }}
    >
      {/* Toggle button */}
      <div className="flex items-center justify-between p-3 border-b border-border">
        {isOpen && (
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={14} className="text-muted-foreground" />
            <span className="text-xs font-semibold text-foreground">Filters</span>
            <span className="text-xs font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-full tabular-nums">
              {totalCount}
            </span>
          </div>
        )}
        <button
          onClick={onToggle}
          className="p-1.5 rounded-lg hover:bg-muted transition-colors ml-auto"
          aria-label={isOpen ? 'Collapse filters' : 'Expand filters'}
        >
          {isOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>

      {isOpen && (
        <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-6">
          {/* Sort */}
          <div>
            <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Sort By</p>
            <div className="space-y-1">
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={`sort-${opt.value}`}
                  onClick={() => updateFilter('sortBy', opt.value)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-150 ${
                    filters.sortBy === opt.value
                      ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div>
            <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Category</p>
            <div className="space-y-1">
              {CATEGORIES.map((cat) => (
                <button
                  key={`cat-filter-${cat}`}
                  onClick={() => updateFilter('category', cat)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-150 ${
                    filters.category === cat
                      ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Status */}
          <div>
            <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Status</p>
            <div className="space-y-1">
              {STATUSES.map((s) => (
                <button
                  key={`status-filter-${s}`}
                  onClick={() => updateFilter('status', s)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-150 ${
                    filters.status === s
                      ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {STATUS_LABELS[s] ?? s}
                </button>
              ))}
            </div>
          </div>

          {/* Funding Range */}
          <div>
            <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Funding Target</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  Min: ${(filters.fundingMin / 1000).toFixed(0)}K
                </label>
                <input
                  type="range"
                  min={0}
                  max={500000}
                  step={10000}
                  value={filters.fundingMin}
                  onChange={(e) => updateFilter('fundingMin', Number(e.target.value))}
                  className="w-full accent-primary"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  Max: ${(filters.fundingMax / 1000).toFixed(0)}K
                </label>
                <input
                  type="range"
                  min={0}
                  max={1000000}
                  step={10000}
                  value={filters.fundingMax}
                  onChange={(e) => updateFilter('fundingMax', Number(e.target.value))}
                  className="w-full accent-primary"
                />
              </div>
            </div>
          </div>

          {/* Reset */}
          <button
            onClick={() => {
              updateFilter('category', 'All');
              updateFilter('status', 'All');
              updateFilter('fundingMin', 0);
              updateFilter('fundingMax', 1000000);
              updateFilter('sortBy', 'newest');
            }}
            className="w-full text-xs font-semibold text-danger hover:bg-danger-bg px-3 py-2 rounded-lg transition-colors duration-150"
          >
            Reset All Filters
          </button>
        </div>
      )}
    </aside>
  );
}